import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { Box, Flex, Text } from '@chakra-ui/core';
import { PageDefault } from '../common/components/layouts/Page/PageDefault';
import { CompanyForm } from '../common/components/CompanyForm/CompanyForm';
import { PrimaryButton, SecondaryButton } from '../common/components/Buttons';
import {
  ActionModal,
  ActionModalBody,
  ActionModalCloseButton,
  ActionModalContent,
  ActionModalFooter,
  ActionModalHeader,
  ActionModalOverlay,
} from '../common/components/ActionModal';
import { companiesActions } from '../redux/companies/actions';
import { companiesAPI } from '../common/api/companies';
import routes from '../common/routes';

const CompanyCreate = ({ error, pending, createCompany, departments, qualifications }) => {
  const [similarCompanies, setSimilarCompanies] = useState([]);
  const [pendingValues, setPendingValues] = useState(null);
  const [checking, setChecking] = useState(false);

  const closeWarning = () => {
    setSimilarCompanies([]);
    setPendingValues(null);
  };

  const onSubmit = async newCompanyValues => {
    setChecking(true);
    const similar = await companiesAPI
      .getSimilarCompanies(newCompanyValues.name, newCompanyValues.email)
      .catch(() => []);
    setChecking(false);
    if (similar.length) {
      setSimilarCompanies(similar);
      setPendingValues(newCompanyValues);
      return;
    }
    createCompany(newCompanyValues);
  };

  const createAnyway = () => {
    const values = pendingValues;
    closeWarning();
    createCompany(values);
  };

  return (
    <PageDefault title="Création de la fiche entreprise">
      <CompanyForm
        qualifications={qualifications}
        departments={departments}
        initialValues={{
          certification: 'false',
          hasAccessToProjectPlace: 'false',
          hasPriorityAccessToProjectPlace: 'false',
          hasNotifications: 'true',
          hasSmsNotifications: 'false',
          departments: [],
        }}
        pending={pending || checking}
        onSubmit={onSubmit}
        error={error}
        mode="create"
      />
      <ActionModal isOpen={similarCompanies.length > 0} onClose={closeWarning}>
        <ActionModalOverlay />
        <ActionModalContent data-testid="similar-companies-modal">
          <ActionModalHeader>
            {similarCompanies.length > 1
              ? 'Des entreprises proches existent déjà'
              : `L'entreprise ${similarCompanies[0]?.name} existe déjà`}
          </ActionModalHeader>
          <ActionModalCloseButton />
          <ActionModalBody>
            <Text mb={4}>
              Voulez-vous utiliser la fiche existante plutôt que d&apos;en créer une nouvelle ? Deux
              fiches pour la même entreprise séparent ses devis et ses documents.
            </Text>
            {similarCompanies.map(company => (
              <Flex
                key={company.id}
                justify="space-between"
                align="center"
                py={2}
                borderBottom="1px solid"
                borderColor="lightGray3"
              >
                <Box>
                  <Text fontWeight="bold">{company.name}</Text>
                  <Text fontSize="sm" color="gray.600">
                    {company.email}
                  </Text>
                </Box>
                <SecondaryButton as={Link} to={`${routes.companies}/${company.id}/edit`}>
                  Ouvrir la fiche
                </SecondaryButton>
              </Flex>
            ))}
          </ActionModalBody>
          <ActionModalFooter>
            <SecondaryButton type="button" mr={3} onClick={closeWarning}>
              Annuler
            </SecondaryButton>
            <PrimaryButton type="button" onClick={createAnyway}>
              Créer quand même
            </PrimaryButton>
          </ActionModalFooter>
        </ActionModalContent>
      </ActionModal>
    </PageDefault>
  );
};

const mapStateToProps = ({ companies: state }) => ({
  error: state.error,
  pending: state.pending,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      createCompany: companiesActions.createCompany,
    },
    dispatch,
  );

CompanyCreate.propTypes = {
  pending: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  error: PropTypes.object,
  createCompany: PropTypes.func.isRequired,
};

CompanyCreate.defaultProps = {
  pending: false,
  error: {},
};

export default connect(mapStateToProps, mapDispatchToProps)(CompanyCreate);
